import {
    Table,
    TableBody,
    TableCell,
    TableColumn,
    TableHeader,
    TableRow,
    Input,
    Select,
    SelectItem,
    Chip,
    Spinner,
    Tooltip,
    Modal,
    ModalContent,
    ModalHeader,
    ModalBody,
    ModalFooter,
    Checkbox,
    Switch,
} from "@heroui/react";
import { useEffect, useMemo, useState } from "react";
import { Building, EnvelopeSimple, CheckCircle, WarningCircle, EnvelopeOpen, UserGear, X } from "@phosphor-icons/react";
import { fetchWithAuth } from "shared/api/fetch-with-auth";
import { API_URL } from "constants/index";
import { useSession } from "@clerk/clerk-react";
import { DatePickerInput } from "shared/components/DatePickerInput/DatePickerInput";
import { UniversalTableSearch } from "shared/components/UniversalTable";
import type { UniversalFilterSection } from "shared/components/UniversalTable";

import { ContractPhase, OutGoingRentalFileStatus, RentalContract } from "shared/types/rental-contracts";
import { applyMask } from "shared/utils/masks";
import { MilestoneDateType } from "shared/types/rental-contracts";
import { toast } from "react-toastify";
import ellaIcon from "screens/kaylee/ellaAIKayleeUp.png";
import RecentlyCompletedOutgoingTenants from "./recently-completed-outgoing-tenants";
import OutgoingChecklistPreviewModal from "./outgoing-checklist-preview-modal";
import UniversalPickerModal from "shared/components/UniversalPickerModal/UniversalPickerModal";
import { useActiveUsers } from "shared/contexts/users.context";

export interface OutgoingTenantsAdministrationProps { }

const OutgoingTenantsAdministration = ({ }: OutgoingTenantsAdministrationProps) => {
    const [contracts, setContracts] = useState<RentalContract[]>([]);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const { session } = useSession();
    const { users: activeUsers } = useActiveUsers();
    const [guaranteeInputs, setGuaranteeInputs] = useState<{ [key: number]: string }>({});
    const [visitationDaysInputs, setVisitationDaysInputs] = useState<{ [key: number]: string }>({});
    const [responsiblePickerContractId, setResponsiblePickerContractId] = useState<number | null>(null);
    const [isCompleteModalOpen, setIsCompleteModalOpen] = useState(false);
    const [selectedContractId, setSelectedContractId] = useState<number | null>(null);
    const [checklistItems, setChecklistItems] = useState({
        keysReceived: false,
        guaranteeReleased: false,
        inspectionCompleted: false,
        noRentArrears: false,
        maintenanceCertificate: false,
    });
    const [selectedEmailId, setSelectedEmailId] = useState<string | null>(null);
    const [isEmailPreviewOpen, setIsEmailPreviewOpen] = useState(false);
    const [selectedContract, setSelectedContract] = useState<RentalContract | null>(null);
    const [searchText, setSearchText] = useState<string>("");
    const [activeFilters, setActiveFilters] = useState<Record<string, string[]>>({
        checklistSent: [],
        inspectionPlanned: [],
        responsible: [],
    });

    const fetchOutgoingTenants = async () => {
        console.log(selectedEmailId)
        try {
            setIsLoading(true);
            console.log("Fetching outgoing tenants...");

            const response = await fetchWithAuth(
                `${API_URL}/rental/outgoing-tenants`,
                { method: "GET" },
                session
            );
            console.log("Response status:", response.status);

            if (!response.ok) {
                throw new Error(`Failed to fetch outgoing tenants: ${response.status}`);
            }

            const data = await response.json();
            console.log("Fetched outgoing tenants data:", {
                count: data?.length || 0,
                contracts: data?.map((contract: any) => ({
                    id: contract.id,
                    unitId: contract.unit?.id,
                    phase: contract.phase,
                    outgoingRentalFileStatus: contract.outgoingRentalFileStatus,
                    tenants: contract.contractContacts
                        ?.filter((contact: any) => contact.relatedContact)
                        .map((contact: any) =>
                            `${contact.relatedContact?.firstName} ${contact.relatedContact?.lastName}`
                        )
                }))
            });

            setContracts(data);
        } catch (error) {
            console.error("Failed to fetch outgoing tenants:", error);
        } finally {
            setIsLoading(false);
        }
    };

    const updateContract = async (contractId: number, updates: Partial<RentalContract>) => {
        try {
            const response = await fetchWithAuth(
                `${API_URL}/rental/contracts/${contractId}/outgoing-administration`,
                {
                    method: "PUT",
                    body: JSON.stringify(updates),
                    headers: {
                        'Content-Type': 'application/json'
                    }
                },
                session
            );
            if (!response.ok) {
                throw new Error('Failed to update contract');
            }
            fetchOutgoingTenants(); // Refresh data
        } catch (error) {
            console.error("Failed to update contract:", error);
        }
    };

    const sendChecklistEmail = async (contractId: number) => {
        try {
            const response = await fetchWithAuth(
                `${API_URL}/rental/contracts/${contractId}/send-checklist`,
                { method: "POST" },
                session
            );
            if (!response.ok) {
                // De backend legt uit wát er scheelt (geen e-mailadres bij de
                // huurders, checklijst niet gevonden in SharePoint, ...). Die
                // uitleg moet de gebruiker zien: anders lijkt de mail verzonden.
                const body = await response.json().catch(() => null);
                throw new Error(body?.message || `Versturen mislukt (${response.status})`);
            }
            toast.success("Checklijst verstuurd naar de huurder");
            fetchOutgoingTenants(); // Refresh data
        } catch (error) {
            console.error("Failed to send checklist:", error);
            toast.error(
                error instanceof Error
                    ? error.message
                    : "Kon de checklijst niet versturen."
            );
        }
    };

    const toggleEllaCalendar = async (contractId: number, hasEvent: boolean) => {
        try {
            console.log(`${hasEvent ? 'Removing from' : 'Adding to'} calendar for contract ${contractId}`);

            const response = await fetchWithAuth(
                `${API_URL}/rental/contracts/${contractId}/${hasEvent ? 'remove-from-calendar' : 'add-to-calendar'}`,
                {
                    method: "POST",
                    headers: {
                        'Content-Type': 'application/json'
                    }
                },
                session
            );

            if (!response.ok) {
                throw new Error(`Failed to ${hasEvent ? 'remove from' : 'add to'} calendar`);
            }

            console.log(`Successfully ${hasEvent ? 'removed from' : 'added to'} calendar`);
            fetchOutgoingTenants(); // Refresh data
        } catch (error) {
            console.error(`Failed to ${hasEvent ? 'remove from' : 'add to'} calendar:`, error);
        }
    };

    const clearInspectionDate = async (contractId: number, hasCalendarEvent: boolean) => {
        try {
            if (hasCalendarEvent) {
                const calResponse = await fetchWithAuth(
                    `${API_URL}/rental/contracts/${contractId}/remove-from-calendar`,
                    { method: "POST", headers: { 'Content-Type': 'application/json' } },
                    session
                );
                if (!calResponse.ok) throw new Error('Failed to remove from calendar');
            }
            const response = await fetchWithAuth(
                `${API_URL}/rental/contracts/${contractId}/outgoing-administration`,
                {
                    method: "PUT",
                    body: JSON.stringify({ outgoingInspectionDate: null }),
                    headers: { 'Content-Type': 'application/json' }
                },
                session
            );
            if (!response.ok) throw new Error('Failed to clear inspection date');
            fetchOutgoingTenants();
        } catch (error) {
            console.error("Failed to clear inspection date:", error);
        }
    };

    const handleCompleteFile = async () => {
        if (!selectedContractId) return;

        try {
            const response = await fetchWithAuth(
                `${API_URL}/rental/contracts/${selectedContractId}/toggle-outgoing-file-completion`,
                {
                    method: "POST",
                    headers: {
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify({
                        completed: true,
                        checklistItems
                    })
                },
                session
            );

            if (!response.ok) {
                throw new Error('Failed to complete file');
            }

            setIsCompleteModalOpen(false);
            setChecklistItems({
                keysReceived: false,
                guaranteeReleased: false,
                inspectionCompleted: false,
                noRentArrears: false,
                maintenanceCertificate: false,
            });
            setSelectedContractId(null);
            fetchOutgoingTenants(); // Refresh data
        } catch (error) {
            console.error("Failed to complete file:", error);
        }
    };

    useEffect(() => {
        fetchOutgoingTenants();
    }, []);

    const getTenantsText = (contract: RentalContract): string => {
        return (contract.contractContacts ?? [])
            .filter((c: any) => c.relatedContact)
            .map((c: any) => `${c.relatedContact?.firstName ?? ""} ${c.relatedContact?.lastName ?? ""}`.trim())
            .join(", ");
    };

    const getResponsibleEmail = (contract: RentalContract): string | null => {
        return contract.outgoingInspectionResponsibleEmail ?? null;
    };

    const filterSections: UniversalFilterSection[] = useMemo(() => ([
        {
            key: "checklistSent",
            label: "Checklist verzonden",
            color: "moss",
            options: [
                { key: "sent", label: "Verzonden" },
                { key: "not-sent", label: "Niet verzonden" },
            ],
        },
        {
            key: "inspectionPlanned",
            label: "Uitgaande plaatsbeschrijving",
            color: "yellow",
            options: [
                { key: "planned", label: "Ingepland" },
                { key: "not-planned", label: "Nog in te plannen" },
            ],
        },
        {
            key: "responsible",
            label: "Verantwoordelijke",
            color: "neutral",
            options: [
                { key: "unassigned", label: "Niet toegewezen" },
                ...activeUsers.map((u: any) => ({
                    key: u.email_addresses?.[0]?.email_address ?? u.id,
                    label: `${u.first_name ?? ""} ${u.last_name ?? ""}`.trim() || (u.email_addresses?.[0]?.email_address ?? ""),
                })),
            ],
        },
    ]), [activeUsers]);

    const filteredContracts = useMemo(() => {
        const term = searchText.trim().toLowerCase();
        return contracts.filter((contract) => {
            if (term) {
                const haystack = [
                    contract.unit?.id?.toString() ?? "",
                    getTenantsText(contract).toLowerCase(),
                ].join(" ");
                if (!haystack.toLowerCase().includes(term)) return false;
            }

            const checklistFilter = activeFilters.checklistSent;
            if (checklistFilter.length > 0) {
                const isSent = Boolean(contract.outgoingInspectionChecklistSent);
                const matchesSent = checklistFilter.includes("sent") && isSent;
                const matchesNotSent = checklistFilter.includes("not-sent") && !isSent;
                if (!matchesSent && !matchesNotSent) return false;
            }

            const plannedFilter = activeFilters.inspectionPlanned;
            if (plannedFilter.length > 0) {
                const isPlanned = Boolean(contract.outgoingInspectionDate);
                const matchesPlanned = plannedFilter.includes("planned") && isPlanned;
                const matchesNotPlanned = plannedFilter.includes("not-planned") && !isPlanned;
                if (!matchesPlanned && !matchesNotPlanned) return false;
            }

            const responsibleFilter = activeFilters.responsible;
            if (responsibleFilter.length > 0) {
                const email = getResponsibleEmail(contract);
                const matchesUnassigned = responsibleFilter.includes("unassigned") && !email;
                const matchesEmail = email !== null && responsibleFilter.includes(email);
                if (!matchesUnassigned && !matchesEmail) return false;
            }

            return true;
        });
    }, [contracts, searchText, activeFilters]);

    const StatusDot = ({ isComplete }: { isComplete: boolean }) => (
        <div
            className={`h-3 w-3 rounded-full ${isComplete ? "bg-green-500" : "bg-red-500"
                }`}
        />
    );

    const formatDateDDMMYYYY = (date: Date) => {
        return date.toLocaleDateString('nl-BE', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric'
        });
    };

    const closeEmailPreview = () => {
        setIsEmailPreviewOpen(false);
        setSelectedContract(null);
    };

    if (isLoading) {
        return (
            <div className="flex h-48 items-center justify-center">
                <Spinner
                    label="Gegevens aan het ophalen"
                    color="success"
                    size="md"
                />
            </div>
        );
    }

    if (!contracts || contracts.length === 0) {
        return (
            <div className="flex flex-col gap-2">
                <header className="flex items-center justify-between">
                    <div className="text-lg font-medium">
                        Administratieve afhandeling uitgaande huurders
                    </div>
                </header>
                <div className="rounded-lg bg-white p-4 text-center text-neutral-primary/60">
                    Geen uitgaande huurders gevonden
                </div>
            </div>
        );
    }

    return (
        <>
            <div className="flex flex-col gap-6">
                <div>
                    <div className="flex items-center justify-between mb-4">
                        <h2 className="text-lg font-medium">Lopende dossiers</h2>
                        <span className="text-sm text-neutral-secondary-mid">
                            {filteredContracts.length} van {contracts.length} dossiers
                        </span>
                    </div>
                    <UniversalTableSearch
                        rows={contracts}
                        searchValue={searchText}
                        onSearchChange={setSearchText}
                        searchPlaceholder="Zoek op unit of huurder..."
                        filterSections={filterSections}
                        activeFilters={activeFilters}
                        onFilterChange={(key, values) =>
                            setActiveFilters((prev) => ({ ...prev, [key]: values }))
                        }
                    />
                    <div className="col-span-full flex flex-col gap-2 overflow-x-auto">
                        <Table aria-label="Outgoing tenants administration table" className="min-w-[1500px]">
                            <TableHeader>
                                <TableColumn>Unit</TableColumn>
                                <TableColumn>Huurder</TableColumn>
                                <TableColumn>Einddatum</TableColumn>
                                <TableColumn>Checklist verzonden</TableColumn>
                                <TableColumn className="min-w-[320px]">Uitgaande plaatsbeschrijving</TableColumn>
                                <TableColumn>Verantw. plaatsbeschrijving</TableColumn>
                                <TableColumn>Bezoekdagen</TableColumn>
                                <TableColumn>Verantwoordelijke</TableColumn>
                                <TableColumn>Huurwaarborg</TableColumn>
                                <TableColumn>In te houden</TableColumn>
                                <TableColumn>Waarborg vrijgegeven</TableColumn>
                                <TableColumn>Acties</TableColumn>
                            </TableHeader>
                            <TableBody emptyContent="Geen dossiers gevonden voor deze zoek- en filteropties.">
                                {filteredContracts.map((contract: RentalContract) => (
                                    <TableRow key={contract.id}>
                                        <TableCell>
                                            <Chip
                                                startContent={<Building weight="duotone" size={14} />}
                                                classNames={{
                                                    base: "h-fit bg-white px-2 border border-moss-primary text-moss-primary",
                                                    content: "px-0 font-medium",
                                                }}
                                            >
                                                {contract.unit?.id}
                                            </Chip>
                                        </TableCell>
                                        <TableCell>
                                            {getTenantsText(contract) || "-"}
                                        </TableCell>
                                        <TableCell>
                                            {contract.contractMilestoneDates?.find((date: any) => date.milestoneDateType === MilestoneDateType.END_DATE)?.date
                                                ? formatDateDDMMYYYY(new Date(contract?.contractMilestoneDates
                                                    ?.find((date: any) => date.milestoneDateType === MilestoneDateType.END_DATE)?.date as any))
                                                : "Geen einddatum"
                                            }
                                        </TableCell>
                                        <TableCell>
                                            <div className="flex items-center gap-2">
                                                <StatusDot isComplete={contract.outgoingInspectionChecklistSent || false} />
                                                {contract.outgoingInspectionChecklistSent ? (
                                                    <div className="flex flex-col">
                                                        <span className="text-sm font-medium">
                                                            {formatDateDDMMYYYY(new Date(contract.outgoingInspectionChecklistSentDate as any))}
                                                        </span>
                                                        <span className="text-xs text-neutral-primary/60">
                                                            {contract.outgoingInspectionChecklistAutoSent ? "Automatisch verzonden" : "Manueel verzonden"}
                                                        </span>
                                                    </div>
                                                ) : (
                                                    <div className="flex items-center gap-2">
                                                        <Tooltip content="Checklist verzenden">
                                                            <button
                                                                onClick={() => {
                                                                    setSelectedContract(contract);
                                                                    setIsEmailPreviewOpen(true);
                                                                }}
                                                                className={`p-2 hover:bg-neutral-100 rounded-full ${
                                                                    contract.outgoingInspectionChecklistSent
                                                                        ? 'text-success'
                                                                        : 'text-neutral-300'
                                                                }`}
                                                            >
                                                                <EnvelopeSimple size={20} />
                                                            </button>
                                                        </Tooltip>
                                                        <Tooltip content="Manueel verzonden">
                                                            <button
                                                                onClick={() => {
                                                                    const date = new Date();
                                                                    updateContract(contract.id as number, {
                                                                        outgoingInspectionChecklistSent: true,
                                                                        outgoingInspectionChecklistSentDate: date,
                                                                        outgoingInspectionChecklistAutoSent: false,
                                                                    });
                                                                }}
                                                                className="p-2 hover:bg-neutral-100 rounded-full text-moss-primary"
                                                            >
                                                                <EnvelopeSimple size={20} />
                                                            </button>
                                                        </Tooltip>
                                                    </div>
                                                )}
                                            </div>
                                            {contract.outgoingChecklistMessageId && (
                                                <Tooltip content="Bekijk antwoorden">
                                                    <button
                                                        onClick={() => setSelectedEmailId(contract.outgoingChecklistMessageId as string)}
                                                        className="p-2 hover:bg-neutral-100 rounded-full"
                                                    >
                                                        <EnvelopeOpen size={20} />
                                                    </button>
                                                </Tooltip>
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            <div className="flex items-center gap-2">
                                                <DatePickerInput
                                                    value={contract.outgoingInspectionDate ?? undefined}
                                                    onChange={(dateStr) => {
                                                        if (dateStr) {
                                                            updateContract(contract.id as number, {
                                                                outgoingInspectionDate: new Date(dateStr),
                                                                outgoingInspectionCalendarEventId: undefined
                                                            });
                                                        }
                                                    }}
                                                    showTime={true}
                                                    placeholder="Nog in te plannen"
                                                    className="flex-1 min-w-0"
                                                />
                                                {contract.outgoingInspectionDate && (
                                                    <>
                                                        <Tooltip content="Datum annuleren">
                                                            <button
                                                                onClick={() => clearInspectionDate(
                                                                    contract.id as number,
                                                                    Boolean(contract.outgoingInspectionCalendarEventId)
                                                                )}
                                                                className="p-2 hover:bg-neutral-100 rounded-full text-danger"
                                                            >
                                                                <X size={20} />
                                                            </button>
                                                        </Tooltip>
                                                        <Tooltip
                                                            content={
                                                                contract.outgoingInspectionCalendarEventId
                                                                    ? "Verwijderen uit Ella's agenda"
                                                                    : "Toevoegen aan Ella's agenda"
                                                            }
                                                        >
                                                            <button
                                                                onClick={() => toggleEllaCalendar(
                                                                    contract.id as number,
                                                                    Boolean(contract.outgoingInspectionCalendarEventId)
                                                                )}
                                                                className="p-2 hover:bg-neutral-100 rounded-full"
                                                            >
                                                                <img
                                                                    src={ellaIcon}
                                                                    alt="Ella's agenda"
                                                                    className={`w-7 h-7 flex-shrink-0 object-contain ${contract.outgoingInspectionCalendarEventId
                                                                        ? 'opacity-100'
                                                                        : 'opacity-50 hover:opacity-75'
                                                                        }`}
                                                                />
                                                            </button>
                                                        </Tooltip>
                                                    </>
                                                )}
                                            </div>
                                        </TableCell>
                                        <TableCell>
                                            {(() => {
                                                const responsible = contract.outgoingInspectionResponsibleEmail
                                                    ? activeUsers.find((u: any) =>
                                                        u.email_addresses?.some((e: any) => e.email_address === contract.outgoingInspectionResponsibleEmail)
                                                      )
                                                    : null;
                                                return (
                                                    <Tooltip content={contract.outgoingInspectionResponsibleEmail || "Klik om verantwoordelijke toe te wijzen"}>
                                                        <button
                                                            onClick={() => setResponsiblePickerContractId(contract.id as number)}
                                                            className="flex items-center gap-2 w-full px-2 py-1.5 rounded-lg border border-neutral-primary/20 hover:border-moss-primary text-small text-left transition-colors"
                                                        >
                                                            {responsible ? (
                                                                <>
                                                                    <img
                                                                        src={responsible.image_url}
                                                                        alt={`${responsible.first_name} ${responsible.last_name}`}
                                                                        className="h-5 w-5 rounded-full object-cover shrink-0"
                                                                    />
                                                                    <span className="truncate text-neutral-primary">{responsible.first_name} {responsible.last_name}</span>
                                                                </>
                                                            ) : (
                                                                <>
                                                                    <UserGear size={16} className="text-neutral-primary/40 shrink-0" />
                                                                    <span className="text-neutral-primary/40">Niet toegewezen</span>
                                                                </>
                                                            )}
                                                        </button>
                                                    </Tooltip>
                                                );
                                            })()}
                                        </TableCell>
                                        <TableCell>
                                            <Input
                                                value={visitationDaysInputs[contract.id as number] ?? contract.visitationDays ?? ""}
                                                onChange={(e) => {
                                                    setVisitationDaysInputs({
                                                        ...visitationDaysInputs,
                                                        [contract.id as number]: e.target.value
                                                    });
                                                }}
                                                onBlur={() => {
                                                    if (visitationDaysInputs[contract.id as number]) {
                                                        updateContract(contract.id as number, {
                                                            visitationDays: visitationDaysInputs[contract.id as number],
                                                        });
                                                    }
                                                }}
                                                placeholder="Bezoekdagen"
                                                classNames={{
                                                    input: "text-small",
                                                    inputWrapper: "border-1 border-neutral-primary/20 shadow-none hover:border-moss-primary focus-within:border-moss-primary!",
                                                }}
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <Select
                                                selectedKeys={[contract.visitationResponsible || 'Onbekend']}
                                                onChange={(e) =>
                                                    updateContract(contract.id as number, {
                                                        visitationResponsible: e.target.value,
                                                    })
                                                }
                                                classNames={{
                                                    trigger: "border-1 border-neutral-primary/20 shadow-none hover:border-moss-primary data-[focus=true]:border-moss-primary",
                                                    value: "text-small",
                                                }}
                                            >
                                                <SelectItem key="Yally">Yally</SelectItem>
                                                <SelectItem key="Huurder">Huurder</SelectItem>
                                                <SelectItem key="Onbekend">Onbekend</SelectItem>
                                            </Select>
                                        </TableCell>
                                        <TableCell>
                                            {applyMask(contract.guarantee?.[0]?.amount, "valuta") || "N/A"}
                                        </TableCell>
                                        <TableCell>
                                            <Input
                                                type="number"
                                                value={guaranteeInputs[contract.id as number] ?? contract.guaranteeAmountToWithhold?.toString() ?? ""}
                                                onChange={(e) => {
                                                    setGuaranteeInputs({
                                                        ...guaranteeInputs,
                                                        [contract.id as number]: e.target.value
                                                    });
                                                }}
                                                onBlur={() => {
                                                    if (guaranteeInputs[contract.id as number]) {
                                                        updateContract(contract.id as number, {
                                                            guaranteeAmountToWithhold: parseFloat(guaranteeInputs[contract.id as number]),
                                                        });
                                                    }
                                                }}
                                                placeholder="Bedrag"
                                                classNames={{
                                                    input: "text-small",
                                                    inputWrapper: "border-1 border-neutral-primary/20 shadow-none hover:border-moss-primary focus-within:border-moss-primary!",
                                                }}
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <div className="flex items-center gap-2">
                                                <Switch
                                                    isSelected={contract.guaranteeReleased}
                                                    onValueChange={(isSelected) => {
                                                        updateContract(contract.id as number, {
                                                            guaranteeReleased: isSelected,
                                                        });
                                                    }}
                                                    size="sm"
                                                    color="success"
                                                >
                                                    {contract.guaranteeReleased ? "Vrijgegeven" : "Niet vrijgegeven"}
                                                </Switch>
                                            </div>
                                        </TableCell>
                                        <TableCell>
                                            {!((contract.outgoingRentalFileStatus as OutGoingRentalFileStatus) == OutGoingRentalFileStatus.COMPLETED) && (
                                                <Tooltip content="Dossier voltooien">
                                                    <button
                                                        onClick={() => {
                                                            setSelectedContractId(contract.id as number);
                                                            setIsCompleteModalOpen(true);
                                                        }}
                                                        className="p-2 hover:bg-neutral-100 rounded-full text-moss-primary"
                                                    >
                                                        <CheckCircle size={20} />
                                                    </button>
                                                </Tooltip>
                                            )}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </div>
                </div>

                <div>
                    <h2 className="text-lg font-medium mb-4">Recent afgeronde dossiers</h2>
                    <RecentlyCompletedOutgoingTenants />
                </div>
            </div>

            <Modal
                isOpen={isCompleteModalOpen}
                onClose={() => {
                    setIsCompleteModalOpen(false);
                    setChecklistItems({
                        keysReceived: false,
                        guaranteeReleased: false,
                        inspectionCompleted: false,
                        noRentArrears: false,
                        maintenanceCertificate: false,
                    });
                    setSelectedContractId(null);
                }}
                size="md"
            >
                <ModalContent>
                    <ModalHeader className="flex flex-col gap-1">
                        Dossier voltooien
                    </ModalHeader>
                    <ModalBody>
                        {contracts.find(c => c.id === selectedContractId)?.phase !== ContractPhase.CLOSED && (
                            <div className="mb-4 p-3 bg-warning-50 border border-warning-200 rounded-lg">
                                <p className="flex gap-2 text-sm text-warning-700 font-medium">
                                    <WarningCircle /> Dit contract staat nog open in de boekhouding en zal onmiddellijk beëindigd worden!
                                </p>
                            </div>
                        )}
                        <p className="text-sm text-neutral-600 mb-4">
                            Gelieve te bevestigen dat aan alle voorwaarden is voldaan:
                        </p>
                        <div className="flex flex-col gap-3">
                            <Checkbox
                                isSelected={checklistItems.keysReceived}
                                onValueChange={(value) => setChecklistItems(prev => ({ ...prev, keysReceived: value }))}
                            >
                                De sleutel werd ontvangen
                            </Checkbox>
                            <Checkbox
                                isSelected={checklistItems.guaranteeReleased}
                                onValueChange={(value) => setChecklistItems(prev => ({ ...prev, guaranteeReleased: value }))}
                            >
                                De waarborg werd vrijgegeven
                            </Checkbox>
                            <Checkbox
                                isSelected={checklistItems.inspectionCompleted}
                                onValueChange={(value) => setChecklistItems(prev => ({ ...prev, inspectionCompleted: value }))}
                            >
                                De uitgaande plaatsbeschrijving heeft plaatsgevonden
                            </Checkbox>
                            <Checkbox
                                isSelected={checklistItems.noRentArrears}
                                onValueChange={(value) => setChecklistItems(prev => ({ ...prev, noRentArrears: value }))}
                            >
                                Alle huurachterstallen zijn aangezuiverd
                            </Checkbox>
                            <Checkbox
                                isSelected={checklistItems.maintenanceCertificate}
                                onValueChange={(value) => setChecklistItems(prev => ({ ...prev, maintenanceCertificate: value }))}
                            >
                                Er is een geldig onderhoudsattest aanwezig van de CV-ketel
                            </Checkbox>
                        </div>
                    </ModalBody>
                    <ModalFooter>
                        <button
                            onClick={() => setIsCompleteModalOpen(false)}
                            className="px-4 py-2 rounded-lg border border-neutral-200 hover:bg-neutral-100"
                        >
                            Annuleren
                        </button>
                        <button
                            onClick={handleCompleteFile}
                            disabled={!Object.values(checklistItems).every(Boolean)}
                            className={`px-4 py-2 rounded-lg bg-moss-primary text-white ${!Object.values(checklistItems).every(Boolean)
                                ? 'opacity-50 cursor-not-allowed'
                                : 'hover:bg-moss-primary/90'
                                }`}
                        >
                            Bevestigen
                        </button>
                    </ModalFooter>
                </ModalContent>
            </Modal>
            <OutgoingChecklistPreviewModal
                isOpen={isEmailPreviewOpen}
                contractId={selectedContract?.id ?? null}
                unitId={selectedContract?.unit?.id}
                onClose={closeEmailPreview}
                onConfirm={(contractId) => {
                    closeEmailPreview();
                    sendChecklistEmail(contractId);
                }}
            />
            {responsiblePickerContractId !== null && (
                <UniversalPickerModal
                    isOpen={true}
                    mode="assignee"
                    title="Selecteer verantwoordelijke plaatsbeschrijving"
                    onClose={() => setResponsiblePickerContractId(null)}
                    onConfirm={(user: any) => {
                        if (user) {
                            const email = user.email_addresses?.[0]?.email_address ?? null;
                            updateContract(responsiblePickerContractId, {
                                outgoingInspectionResponsibleEmail: email,
                            });
                        } else {
                            updateContract(responsiblePickerContractId, {
                                outgoingInspectionResponsibleEmail: null as any,
                            });
                        }
                        setResponsiblePickerContractId(null);
                    }}
                    config={{}}
                />
            )}
        </>
    );
};

export default OutgoingTenantsAdministration; 